// 자동 퀘스트 진행 길 안내(시안 C7): 영웅 발밑에서 목적지까지 바닥의 금색 점선(앞으로 흐르는 맥동)과 목적지 위 금색 표식.
// UI(src/ui/autoQuest.ts)가 shared/autoRoute에 적은 경로를 읽기만 한다. 매 프레임 새 객체를 만들지 않는다.
import * as THREE from 'three/webgpu';
import { autoRoute, type AutoIcon } from '../shared/autoRoute';

/** 바닥에서 띄우는 높이(m). 바닥 높이는 맵마다 달라서 영웅 발 높이를 기준으로 삼는다(마을 돌바닥은 0보다 높다) */
const MAX_DOTS = 72, SPACING = 0.95, LIFT = 0.08;
// 24x24 그림(SVG 경로): 모루·물약·검·성문·교차한 검
const ICON_PATH: Record<Exclude<AutoIcon, ''>, string> = {
  forge: 'M3 7h14c0 2.6 1.6 4 4 4v2c-2.8 0-4.5-.9-5.4-2.4L14 12v2l2 2v2H6v-2l2-2v-2C5.3 12 3 10.3 3 7Z',
  shop: 'M9 2h6v2h-1v3.2c3 1 5 3.6 5 6.8a7 7 0 0 1-14 0c0-3.2 2-5.8 5-6.8V4H9Z',
  trainer: 'M19.5 2.5 21.5 4.5 9 17l2 2-1.5 1.5-2-2L5 21l-2-2 2.5-2.5-2-2L5 13l2 2Z',
  gate: 'M3 21V9l2-1V5h3v2h2V4h4v3h2V5h3v3l2 1v12h-7v-5a2 2 0 0 0-4 0v5Z',
  room: 'M4 2 2 4l7 7-3 3-2-1-1 1 3 3-2 2 1 1 2-2 3 3 1-1-1-2 3-3 3 3-1 2 1 1 3-3 2 2 1-1-2-2 3-3-1-1-2 1-3-3 7-7-2-2-7 7Z',
};

function markerTexture(icon: Exclude<AutoIcon, ''>): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128; c.height = 168;
  const g = c.getContext('2d')!;
  // 아래쪽 화살표
  g.fillStyle = '#ffd76a';
  g.beginPath(); g.moveTo(46, 122); g.lineTo(82, 122); g.lineTo(64, 158); g.closePath(); g.fill();
  g.strokeStyle = '#7a4a10'; g.lineWidth = 3; g.stroke();
  // 금테 원판
  const grad = g.createRadialGradient(54, 50, 6, 64, 64, 58);
  grad.addColorStop(0, '#2c3a68'); grad.addColorStop(1, '#0b1024');
  g.fillStyle = grad;
  g.beginPath(); g.arc(64, 64, 54, 0, Math.PI * 2); g.fill();
  g.lineWidth = 8; g.strokeStyle = '#e2b24a'; g.stroke();
  g.lineWidth = 2; g.strokeStyle = '#fff1b8'; g.beginPath(); g.arc(64, 64, 47, 0, Math.PI * 2); g.stroke();
  // 그림
  g.save(); g.translate(34, 34); g.scale(2.5, 2.5);
  g.fillStyle = '#ffd76a';
  g.fill(new Path2D(ICON_PATH[icon]));
  g.restore();
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export class AutoRouteFx {
  readonly group = new THREE.Group();
  private readonly dots: THREE.InstancedMesh;
  private readonly halos: THREE.InstancedMesh;
  private readonly ring: THREE.Mesh;
  private readonly marker: THREE.Sprite;
  private readonly markerMat: THREE.SpriteMaterial;
  private readonly textures = new Map<string, THREE.CanvasTexture>();
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
  private readonly p = new THREE.Vector3();
  private readonly s = new THREE.Vector3();
  private icon: AutoIcon = '';

  constructor() {
    const dg = new THREE.CircleGeometry(0.24, 18);
    const dm = new THREE.MeshBasicNodeMaterial({ color: 0xfff0a0, transparent: true, opacity: 0.97, depthWrite: false, fog: false });
    this.dots = new THREE.InstancedMesh(dg, dm, MAX_DOTS);
    const hm = new THREE.MeshBasicNodeMaterial({ color: 0xffb83a, transparent: true, opacity: 0.55, depthWrite: false, blending: THREE.AdditiveBlending, fog: false });
    this.halos = new THREE.InstancedMesh(new THREE.CircleGeometry(0.5, 20), hm, MAX_DOTS);
    for (const im of [this.dots, this.halos]) { im.frustumCulled = false; im.count = 0; im.renderOrder = 3; this.group.add(im); }
    this.ring = new THREE.Mesh(new THREE.RingGeometry(0.7, 0.86, 40), new THREE.MeshBasicNodeMaterial({ color: 0xffd76a, transparent: true, opacity: 0.8, depthWrite: false, side: THREE.DoubleSide, fog: false }));
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.renderOrder = 3;
    this.markerMat = new THREE.SpriteMaterial({ transparent: true, depthWrite: false, depthTest: false, fog: false });
    this.marker = new THREE.Sprite(this.markerMat);
    this.marker.scale.set(1.15, 1.5, 1);
    this.marker.renderOrder = 10;
    this.group.add(this.ring, this.marker);
    this.group.visible = false;
  }

  /** nowMs: 벽시계(맥동). hx,hz: 보간한 영웅 위치, gy: 영웅 발(접지) 높이 */
  update(nowMs: number, hx: number, hz: number, gy: number): void {
    const r = autoRoute;
    if (!r.active || r.count === 0) { this.group.visible = false; return; }
    this.group.visible = true;
    const t = nowMs / 1000;
    const Y = gy + LIFT;
    // 영웅 → 꺾는 점들을 따라 SPACING 간격으로 점을 놓는다. 앞으로 흐르는 맥동(거리 - 시간)
    let n = 0, ax = hx, az = hz, carry = 0.6, dist = 0;
    for (let i = 0; i < r.count && n < MAX_DOTS; i++) {
      const bx = r.xs[i], bz = r.zs[i], seg = Math.hypot(bx - ax, bz - az);
      let d = carry;
      while (d <= seg && n < MAX_DOTS) {
        const k = seg > 0 ? d / seg : 0;
        const wave = Math.max(0, Math.sin((dist + d) * 1.3 - t * 5));
        // 영웅 발밑 1.2m는 작게(몸에 가리지 않게 겹쳐 번지지 않도록), 그 앞은 일정 크기 + 맥동
        const near = Math.min(1, (dist + d) / 1.2);
        const sc = (0.85 + 0.35 * wave) * (0.45 + 0.55 * near);
        this.p.set(ax + (bx - ax) * k, Y, az + (bz - az) * k);
        this.s.set(sc, sc, sc);
        this.m.compose(this.p, this.q, this.s);
        this.dots.setMatrixAt(n, this.m);
        this.s.set(sc * (0.8 + wave * 0.6), sc * (0.8 + wave * 0.6), 1);
        this.m.compose(this.p, this.q, this.s);
        this.halos.setMatrixAt(n, this.m);
        n++; d += SPACING;
      }
      carry = d - seg; dist += seg; ax = bx; az = bz;
    }
    this.dots.count = this.halos.count = n;
    this.dots.instanceMatrix.needsUpdate = this.halos.instanceMatrix.needsUpdate = true;
    // 목적지: 바닥 고리(맥동)와 떠 있는 표식
    const pulse = 1 + 0.12 * Math.sin(t * 4);
    this.ring.position.set(r.tx, Y + 0.01, r.tz);
    this.ring.scale.set(pulse, pulse, 1);
    if (r.icon !== this.icon) {
      this.icon = r.icon;
      if (r.icon) {
        let tex = this.textures.get(r.icon);
        if (!tex) { tex = markerTexture(r.icon); this.textures.set(r.icon, tex); }
        this.markerMat.map = tex;
        this.markerMat.needsUpdate = true;
      }
    }
    this.marker.visible = r.icon !== '';
    this.marker.position.set(r.tx, gy + (r.icon === 'gate' ? 3.9 : 3.1) + Math.sin(t * 2.5) * 0.12, r.tz);
  }
}

